import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import omit from "lodash-es/omit";

type BaseLabelProps = {
  size?: "base" | "sm" | "lg" | "xs";
  layoutClassName?: string;
  UNSAFE_className?: string;
  variant?: "default" | "inline";
  breakWords?: boolean;
  truncate?: boolean;
  pointer?: boolean;
  label?: React.ReactNode;
};

type LabelAsLabelProps = BaseLabelProps & {
  as?: "label";
} & React.LabelHTMLAttributes<HTMLLabelElement>;

type LabelAsDivProps = BaseLabelProps & {
  as: "div";
} & React.HTMLAttributes<HTMLDivElement>;

type LabelComponent = {
  (
    props: LabelAsLabelProps & { ref?: React.Ref<HTMLLabelElement> },
  ): React.ReactNode;
  (
    props: LabelAsDivProps & { ref?: React.Ref<HTMLDivElement> },
  ): React.ReactNode;
  displayName?: string;
};

const DeprecatedLabel: LabelComponent = React.forwardRef<
  HTMLLabelElement | HTMLDivElement,
  LabelAsLabelProps | LabelAsDivProps
>((props, ref) => {
  const {
    size = "base",
    label,
    layoutClassName,
    UNSAFE_className,
    variant = "default",
    breakWords = false,
    truncate = false,
    pointer = false,
    ...otherProps
  } = props;

  // NOTE (Jackson, 4/3/25) Applying truncate (which handles text overflow)
  //    directly to a flex container like the <label> doesn't work reliably
  //    because flex layout rules can interfere with the overflow calculation.
  //    By moving the truncate styles to a simple inner <span> that just holds
  //    the text, we isolate the text handling from the flex layout, which
  //    allows the browser to correctly detect overflow within the span's
  //    constrained space and apply the ellipsis.
  const innerSpan = (
    <span className={twMerge("min-w-0", truncate && "truncate")}>{label}</span>
  );
  const className = twMerge(
    "text-default flex items-center",
    fontClassNames[size][variant],
    breakWords && "break-words",
    pointer && "cursor-pointer",
    layoutClassName,
    UNSAFE_className,
  );
  if (otherProps.as === "div") {
    return (
      <div
        {...omit(otherProps, "as")}
        className={className}
        ref={ref as React.Ref<HTMLDivElement>}
      >
        {innerSpan}
      </div>
    );
  }

  return (
    <label
      {...omit(otherProps, "as")}
      className={className}
      ref={ref as React.Ref<HTMLLabelElement>}
    >
      {innerSpan}
    </label>
  );
});

const fontClassNames = {
  sm: {
    default: "text-xs font-medium",
    inline: "text-xs",
  },
  base: {
    default: "text-sm font-medium",
    inline: "text-sm",
  },
  lg: {
    default: "text-base",
    inline: "text-base",
  },
  xs: {
    default: "text-2xs font-medium text-muted",
    inline: "text-2xs font-medium text-muted",
  },
};

DeprecatedLabel.displayName = "DeprecatedLabel";

export default DeprecatedLabel;
